import { createModel } from '@rematch/core';
import { MeasurementUnitEnum } from 'domains/measurementDomain';
import { clearCanvaSessionDraftId } from 'utils/canvaSession';
import {
  GallerySku,
  SkuGalleryLabel,
  SkuGalleryLabelCount,
} from 'domains/gallerySkuDomain';
import {
  AddSharedParam,
  ChangeJobNameParam,
  CompareWithOrgParam,
  DeleteSharedParam,
  JobConfig,
  JobConfigQuantityItemShippingCalendar,
  JobConfigShippingCalendar,
  JobConfigSourceEnum,
  JobConfigViewer,
  JobEstimatorConfirmPaperDTO,
  JobEstimatorConfirmPaperUpdateParams,
  JobShippingSpec,
  JobSpecItem,
  JobSpecItemExpression,
  JobSvcOption,
  MaterialsBasePaperOption,
  OptionPapers,
  OptionPapersList,
  PROOF_TYPE_ENUM,
  QuantityPayload,
  SearchCalendarParam,
  SearchScopeEnum,
  SendSharedListParam,
  ShippingCalendarDateService,
  StartJobConfigParam,
  UpdateJobConfigVersion,
  UpdateJobSpecParam,
} from 'domains/jobconfigDomain';
import { Contact } from 'domains/orgDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { SKU } from 'domains/skuDomain';
import { cloneDeep, each, find, merge, minBy, sum } from 'lodash-es';
import moment, { Moment } from 'moment';
import contactService from 'services/contactService';
import jobconfigService from 'services/jobconfigService';
import skuService from 'services/skuService';
import { StepInfo, updatePriceMessage } from 'utils/jobConfig';
import { isSameUTCDay } from 'utils/misc';
import type { RootModel } from '.';
import { RevxErrorMessage } from './../utils/errorCodes';
import { BaseState, baseReducers } from './baseReducers';
import jobEstimatorService from '../services/jobEstimatorService';

export enum DrawerShowEnum {
  VIEW_QUOTE = 'VIEW_QUOTE',
  JOB_ESTIMATOR = 'JOB_ESTIMATOR',
  SUPPLIER_COMPARISON = 'SUPPLIER_COMPARISON',
  VIEW_EXPR = 'VIEW_EXPR',
}

interface JobConfigState extends BaseState {
  jobconfigSource: JobConfigSourceEnum;
  stepInfos: StepInfo[];
  step: number;
  calVisible: boolean;
  userAvlContacts: Contact[];
  configItem: JobConfig;
  sku: SKU;
  galleryLabel: SkuGalleryLabelCount | null;
  revxSpinloading: boolean;
  hideJcResult: boolean;
  errorStep: number;
  maxStep: number;
  shippingCalendar: JobConfigQuantityItemShippingCalendar[];
  curSelectedGallerySku: GallerySku | null;
  curSelectedGalleryLabel: SkuGalleryLabelCount | null;
  curSelectedDropdownLabel: SkuGalleryLabel | null;
  drawerShowType: DrawerShowEnum | false;
  expressions: JobSpecItemExpression[];
  expertMode: boolean;
  optionPaper: {
    [SearchScopeEnum.ALL_PAPER]?: {
      list: OptionPapersList[];
      jobConfigId: string;
      targetSkuId: number;
    }[];
    [SearchScopeEnum.MY_PAPER]?: {
      list: OptionPapersList[];
      jobConfigId: string;
      targetSkuId: number;
    }[];
  };
  filterOptionPaper: MaterialsBasePaperOption;
  expertSvcOption: JobSvcOption[];
  isDefaultQuote: boolean;
  isDefaultComparison: boolean;
  partDropdownList: number[];
  paperSelectionVisible: boolean;
  paperSelectionSource: JobEstimatorConfirmPaperDTO;
  /**
   * The unit the wizard is being filled in, mirrored here from its `MeasurementUnitScope`.
   *
   * Every step posts through `updateJobSpec`, and a redux effect cannot read React context - so
   * the one form-level answer to "which unit are these numbers in" has to reach the model
   * somehow. Undefined outside the wizard, where the user's own setting applies.
   */
  enteredUnit?: MeasurementUnitEnum;
}

export const jobConfig = createModel<RootModel>()({
  state: {
    stepInfos: [],
    calVisible: true,
    step: 1,
    errorStep: 0,
    maxStep: 0,
    sku: null,
    configItem: null,
    expertSvcOption: [],
    galleryLabel: null,
    revxSpinloading: false,
    hideJcResult: false,
    shippingCalendar: [],
    drawerShowType: false,
    expressions: [],
    userAvlContacts: [],
    expertMode: false,
    optionPaper: {},
    filterOptionPaper: {},
    isDefaultQuote: false,
    isDefaultComparison: false,
    partDropdownList: [],
    jobconfigSource: JobConfigSourceEnum.SHOP,
    paperSelectionVisible: false,
    paperSelectionSource: {},
    enteredUnit: undefined,
  } as unknown as JobConfigState,
  reducers: {
    ...baseReducers<JobConfigState>(),
    setJobconfigSource: (
      state: JobConfigState,
      source: JobConfigSourceEnum
    ) => {
      state.jobconfigSource = source;
      return state;
    },
    stepNext: (state: JobConfigState) => {
      if (state.step < state.stepInfos.length) {
        state.step = state.step + 1;
        state.maxStep = state.step > state.maxStep ? state.step : state.maxStep;
      }
      return state;
    },
    stepPrev: (state: JobConfigState) => {
      if (state.step > 1) {
        state.step = state.step - 1;
      }
      return state;
    },
    setPartDropdownList: (state: JobConfigState, list: number[]) => {
      state.partDropdownList = list;
      return state;
    },
    stepTo: (state: JobConfigState, step: number) => {
      if (step > 0) {
        state.step = step;
        state.maxStep = state.maxStep > step ? state.maxStep : step;
      }
      return state;
    },
    setIsDefaultQuote: (state: JobConfigState, isDefaultQuote: boolean) => {
      state.isDefaultQuote = isDefaultQuote;
      return state;
    },
    setIsDefaultComparison: (
      state: JobConfigState,
      isDefaultComparison: boolean
    ) => {
      state.isDefaultComparison = isDefaultComparison;
      return state;
    },
    addShipmentReducer: (
      state: JobConfigState,
      payload: {
        specItem: JobSpecItem;
      }
    ) => {
      const { specItem } = payload;

      const curSpecItem = state.configItem.specItems.find(
        (item) => item.serialNumber === specItem.serialNumber
      );

      if (curSpecItem?.spec.shippings) {
        const shippings = curSpecItem?.spec.shippings;
        const lastShipping = shippings[shippings.length - 1];

        const sumQuantity = sum(
          shippings.map((item) => item.itemQuantity || 0)
        );

        const itemQuantity = curSpecItem.quantity - sumQuantity;

        curSpecItem.spec.shippings.push({
          ...lastShipping,
          itemQuantity: itemQuantity > 0 ? itemQuantity : 1,
          carrierService: undefined,
          carrierType: undefined,
          toName: undefined,
          toCity: undefined,
          toAddressLine: undefined,
          toCountryCode: undefined,
          toPostalCode: undefined,
          toCompany: undefined,
          toStateProvinceCode: undefined,
          isAdd: true,
          serialNumber: (lastShipping.serialNumber || 0) + 1,
          fulfillment: {
            ...lastShipping.fulfillment,
            shippingInfo: {
              ...lastShipping.fulfillment?.shippingInfo,
              internalDistribution: undefined,
            },
            // estimatedDeliveryTime: '',
          },
        });
      }

      return state;
    },

    updateShipmentReducer: (
      state: JobConfigState,
      payload: {
        specItem: JobSpecItem;
        shipping: Partial<JobShippingSpec>;
      }
    ) => {
      const { specItem, shipping } = payload;

      const curSpecItem = state.configItem.specItems.find(
        (item) => item.serialNumber === specItem.serialNumber
      );
      const curShipping = curSpecItem?.spec.shippings.find(
        (item) => item.serialNumber === shipping.serialNumber
      );

      merge(curShipping, shipping);

      return state;
    },

    deleteShipmentReducer: (
      state: JobConfigState,
      payload: {
        specItem: JobSpecItem;
        shipping: Partial<JobShippingSpec>;
      }
    ) => {
      const { specItem, shipping } = payload;

      const curSpecItem = state.configItem.specItems.find(
        (item) => item.serialNumber === specItem.serialNumber
      );
      const curShippingIndex = curSpecItem?.spec.shippings.findIndex(
        (item) => item.serialNumber === shipping.serialNumber
      );

      if (curShippingIndex !== undefined && curShippingIndex > -1) {
        curSpecItem?.spec.shippings.splice(curShippingIndex, 1);

        // when only one shipping left, we should return the quanty to the default quantity
        if (curSpecItem?.spec.shippings.length === 1) {
          if (curSpecItem?.spec.shippings[0]) {
            const shipping = curSpecItem?.spec.shippings[0];
            shipping.itemQuantity = curSpecItem.quantity;
          }
        }
      }

      return state;
    },

    updateShippingCalendarReducer: (
      state: JobConfigState,
      payload: {
        newCalendar: JobConfigShippingCalendar;
        quantityItemSerialNumber: number;
        shippingItemSerialNumber: number;
      }
    ) => {
      const index =
        state.shippingCalendar.findIndex(
          (item) =>
            item.quantityItemSerialNumber === payload.quantityItemSerialNumber
        ) || 0;

      let shippingCalendar = state.shippingCalendar[index];

      if (!shippingCalendar) {
        shippingCalendar = state.shippingCalendar[index] = {
          quantity: 100,
          quantityItemSerialNumber: payload.quantityItemSerialNumber,
          shippingCalendars: [],
        };
      }

      const calendarIndex = shippingCalendar.shippingCalendars.findIndex(
        (item) =>
          item.shippingItemSerialNumber === payload.shippingItemSerialNumber
      );

      if (calendarIndex === -1) {
        shippingCalendar.shippingCalendars.push({
          ...payload.newCalendar,
          shippingItemSerialNumber: payload.shippingItemSerialNumber,
        });
      } else {
        shippingCalendar.shippingCalendars[calendarIndex] = {
          ...payload.newCalendar,
          shippingItemSerialNumber: payload.shippingItemSerialNumber,
        };
      }

      return state;
    },

    selectShippingServiceReducer: (
      state: JobConfigState,
      payload: {
        specItem: JobSpecItem;
        jobShippingSpec: JobShippingSpec;
        arrivalDate: Moment;
        service: ShippingCalendarDateService;
      }
    ) => {
      // first update calendars
      const curSpecItem = state.configItem.specItems.find(
        (item) => item.serialNumber === payload.specItem.serialNumber
      );

      const calendar = state.shippingCalendar.find(
        (item) => item.quantityItemSerialNumber === curSpecItem?.serialNumber
      );
      if (!calendar) {
        return state;
      }
      const shippingCalendar = calendar?.shippingCalendars.find(
        (item) =>
          item.shippingItemSerialNumber === payload.jobShippingSpec.serialNumber
      );

      if (!shippingCalendar) {
        return state;
      }
      const date = shippingCalendar?.dates.find((d) =>
        isSameUTCDay(
          moment(d.calendarDate),
          moment(payload.service.deliveryDateTime || payload.arrivalDate)
        )
      );

      if (!date) {
        return state;
      }

      const service = date?.services.find(
        (s) => s.carrierService === payload.service.carrierService
      );

      if (!service) {
        return state;
      }

      (shippingCalendar?.dates || []).forEach((date) => {
        (date.services || []).forEach((s) => {
          s.selected = false;
        });
      });
      if (service) {
        service.selected = true;

        if (curSpecItem?.spec.shippings) {
          const curShipping = curSpecItem.spec.shippings.find((shipment) => {
            return (
              shipment.serialNumber === payload.jobShippingSpec.serialNumber
            );
          });
          if (curShipping) {
            curShipping.carrierService = service.carrierService;
            curShipping.carrierType = service.carrierType;
            curShipping.accountType = service.accountType;
            curShipping.fulfillment.shippingInfo.estimatedDeliveryTime =
              service.deliveryDateTime;
            curShipping.expectedDeliveryDate = service.deliveryDateTime;
            curShipping.fulfillment.shippingInfo.estimatedShipTime =
              service.estimatedShipTime ||
              curShipping.fulfillment.shippingInfo.estimatedShipTime;
            curShipping.fulfillment.shippingInfo.estimatedDeliveryTime =
              service.deliveryDateTime;
            curShipping.fulfillment.shippingInfo.estimatedShipTime =
              service.estimatedShipTime ||
              curShipping.fulfillment.shippingInfo.estimatedShipTime;
          }
        }
      }
      return state;
    },

    updateShippingNoteReducer: (state: JobConfigState, payload: string) => {
      state.configItem.shippingNote = payload;
      return state;
    },

    updateExpertSvcOption: (state: JobConfigState, payload: JobSvcOption) => {
      const oldSvcOptionn = state.expertSvcOption.filter(
        (item) =>
          item.isWrapper !== payload.isWrapper &&
          item.jobConfigId !== payload.jobConfigId &&
          item.targetSkuId !== payload.targetSkuId
      );
      state.expertSvcOption = [...oldSvcOptionn, payload];
      return state;
    },

    reset: (state: JobConfigState) => {
      state.stepInfos = [];
      state.step = 0;
      state.revxSpinloading = false;
      state.calVisible = true;
      state.drawerShowType = false;
      state.hideJcResult = false;
      state.errorStep = 0;
      state.maxStep = 0;
      state.galleryLabel = null;
      (state.configItem as JobConfig | null) = null;
      state.shippingCalendar = [];
      state.expressions = [];
      state.expertMode = false;
      state.optionPaper = {};
      (state.sku as SKU | null) = null;
      state.expertSvcOption = [];
      return state;
    },

    clearOptionPaper: (state: JobConfigState) => {
      state.optionPaper = {};
      return state;
    },
  },

  effects: (dispatch) => {
    const { jobConfig, app } = dispatch;

    return {
      async goToNext(payload, state) {
        // do aync tasks place holder
        jobConfig.stepNext();
      },
      async goToPrev(payload, state) {
        jobConfig.stepPrev();
      },
      async goToStep(payload: number) {
        jobConfig.stepTo(payload);
      },
      setRevxSpinloading(loading: boolean): void {
        jobConfig.setState({
          revxSpinloading: loading,
        });
        if (loading) {
          app.showLoading();
        } else {
          app.hideLoading();
        }
      },
      setHideJcResult(hide: boolean): void {
        jobConfig.setState({
          hideJcResult: hide,
        });
      },
      resetErrorStep(): void {
        jobConfig.setState({
          errorStep: 0,
        });
      },
      setErrorStep(step: number): void {
        jobConfig.setState({
          errorStep: step,
        });
      },
      setDrawerShowType(type: DrawerShowEnum | false): void {
        jobConfig.setState({
          drawerShowType: type,
        });
      },
      async getShippingCalendar(
        payload: {
          shippingItem?: {
            jobShippingSpec: JobShippingSpec;
            specItem: JobSpecItem;
            param: SearchCalendarParam;
          };
        },
        state
      ) {
        const configItem = state.jobConfig.configItem;
        try {
          const calender = await jobconfigService.getShippingCalendar(
            configItem.id
          );

          jobConfig.setState({
            shippingCalendar: calender,
          });
        } catch {
          if (payload.shippingItem) {
            jobConfig.updateShippingCalendars({
              jobShippingSpec: payload.shippingItem.jobShippingSpec,
              specItem: payload.shippingItem.specItem,
              param: payload.shippingItem.param,
            });
          }
        }
      },

      async addQuantity(
        payload: Partial<QuantityPayload>,
        state
      ): Promise<JobConfig | false> {
        const prevItem = state.jobConfig.configItem;
        let res = prevItem;
        try {
          res = await jobconfigService.addQuantity(payload);
          jobConfig.setState({ configItem: res });
          dispatch.jobConfig.getShippingCalendar({});
          return res;
        } catch {
          jobConfig.setState({ configItem: prevItem });
          return false;
        }
      },

      async updateQuantity(
        payload: Partial<QuantityPayload>,
        state
      ): Promise<JobConfig | false> {
        const prevItem = state.jobConfig.configItem;
        try {
          const res = await jobconfigService.updateQuantity(payload);
          jobConfig.setState({ configItem: res });
          dispatch.jobConfig.getShippingCalendar({});
          return res;
        } catch {
          jobConfig.setState({ configItem: prevItem });
          return false;
        }
      },

      async deleteQuantity(
        payload: Partial<QuantityPayload>,
        state
      ): Promise<JobConfig | false> {
        const prevItem = state.jobConfig.configItem;
        try {
          const res = await jobconfigService.deleteQuantity(payload);
          jobConfig.setState({ configItem: res });
          dispatch.jobConfig.getShippingCalendar({});
          return res;
        } catch {
          jobConfig.setState({ configItem: prevItem });
          return false;
        }
      },

      async updateVersion(
        payload: UpdateJobConfigVersion,
        state
      ): Promise<JobConfig | false> {
        const prevItem = state.jobConfig.configItem;
        try {
          const res = await jobconfigService.updateVersion(payload);
          jobConfig.setState({ configItem: res });
          return res;
        } catch {
          jobConfig.setState({ configItem: prevItem });
          return false;
        }
      },

      async updateJobSpec(
        payload: UpdateJobSpecParam,
        state
      ): Promise<JobConfig | false | string> {
        const prevItem = state.jobConfig.configItem;
        if (!payload.dryRunEnabled) {
          jobConfig.setRevxSpinloading(true);
        }
        try {
          const res = await jobconfigService.updateJobSpec(
            // The unit the wizard's selector is set to, for every step that posts a measurement
            // it does not declare itself. A step that does - the size step - overrides it.
            { enteredUnit: state.jobConfig.enteredUnit, ...payload },
            prevItem.id,
            payload.dryRunEnabled
          );
          dispatch.jobConfig.getShippingCalendar({});
          if (payload.dryRunEnabled) {
            jobConfig.setState({ configItem: prevItem });
          } else {
            jobConfig.setState({ configItem: res });
          }
          return res;
        } catch (e) {
          jobConfig.setState({ configItem: prevItem });
          return (e as RevxErrorMessage).message;
        } finally {
          jobConfig.setRevxSpinloading(false);
        }
      },

      async initJobConfig(payload: {
        jobConfigId: string;
        expertMode: boolean;
      }) {
        try {
          const { jobConfigId, expertMode } = payload;
          let configItem, skuDetail;
          if (jobConfigId) {
            // first to get Job Config
            configItem = await jobconfigService.getJobConfigFromCache(
              jobConfigId
            );
            // second to get sku detail
            skuDetail = await skuService.startViewSku(
              {
                id: configItem.specItems[0].spec.skuId,
              },
              true
            );
            jobConfig.reset();
            jobConfig.setState({
              configItem: configItem,
              sku: skuDetail,
              expertMode: !!expertMode,
              step: 1,
              maxStep: 0,
              errorStep: 0,
            });
          }
          return true;
        } catch (e) {
          return false;
        }
      },

      updateShipment: async (
        payload: {
          specItem?: JobSpecItem;
          param?: Partial<JobShippingSpec>;
          updateInternalDistribution?: boolean;
        },
        state
      ): Promise<JobConfig> => {
        const { configItem } = state.jobConfig;
        const { specItem, param, updateInternalDistribution } = payload;
        const quantityItems = (
          specItem ? [specItem] : configItem.specItems
        ).map((specItem) => {
          const newShippings = specItem.spec.shippings.map((shipment) => {
            if (shipment.serialNumber === param?.serialNumber) {
              const internalDistribution = updateInternalDistribution
                ? {
                    ...shipment,
                    ...param,
                  }.internalDistribution
                : {
                    ...{
                      ...shipment,
                      ...param,
                    }?.fulfillment?.shippingInfo?.internalDistribution,
                  };
              return {
                ...shipment,
                ...param,
                internalDistribution: Boolean(
                  Object.values(internalDistribution || {}).filter(
                    (item) => item
                  ).length
                )
                  ? internalDistribution
                  : undefined,
              };
            }
            const internalDistribution = {
              ...shipment?.fulfillment?.shippingInfo?.internalDistribution,
            };
            return {
              ...shipment,
              internalDistribution: Boolean(
                Object.values(internalDistribution || {}).filter((item) => item)
                  .length
              )
                ? internalDistribution
                : undefined,
            };
          });
          return {
            serialNumber: specItem.serialNumber,
            shippingItems: cloneDeep(
              newShippings.filter((item) => item.carrierService)
            ).map((item) => {
              if (item.isAdd) {
                item.serialNumber = undefined;
              }
              return item;
            }),
          };
        });
        // update shipment
        const newConfigItem = await jobconfigService.updateShipping({
          jobConfigId: configItem.id,
          note: configItem.shippingNote || '',
          quantityItems,
        });

        await jobConfig.getShippingCalendar({});
        jobConfig.setState({
          configItem: newConfigItem,
        });

        return newConfigItem;
      },

      validateSpecItemShipments: (
        payload: {
          specItem: JobSpecItem;
          useModel?: boolean;
          overrideJobShippingSpec?: JobShippingSpec;
          overrideAmount?: number;
        },
        state
      ): boolean => {
        const { configItem } = state.jobConfig;
        const { specItem, overrideJobShippingSpec, overrideAmount, useModel } =
          payload;

        let curSpecItem = specItem;
        let curJobShippingSpec = { ...overrideJobShippingSpec };
        let curAmount = overrideAmount;

        if (useModel) {
          const item = configItem.specItems.find(
            (item) => item.serialNumber === specItem.serialNumber
          );

          if (item) {
            curSpecItem = item;
          }

          if (overrideJobShippingSpec) {
            const shippingSpec = item?.spec.shippings.find(
              (item) =>
                item.serialNumber === overrideJobShippingSpec.serialNumber
            );

            if (shippingSpec) {
              curJobShippingSpec = shippingSpec;
            }
            if (shippingSpec?.itemQuantity !== undefined) {
              curAmount = shippingSpec?.itemQuantity;
            }
          }
        }

        curJobShippingSpec.itemQuantity = curAmount;

        // try to persist shipment
        let newShippings = curSpecItem.spec.shippings;
        if (curJobShippingSpec?.serialNumber !== undefined) {
          newShippings = newShippings.map((shipping) => {
            if (shipping.serialNumber === curJobShippingSpec?.serialNumber) {
              return {
                ...shipping,
                ...curJobShippingSpec,
              };
            }
            return shipping;
          });
        }

        // validate amount
        const total = sum(newShippings.map((item) => item.itemQuantity));
        if (total !== curSpecItem.quantity) {
          return false;
        }

        // validate address
        if (
          newShippings.find((item) => !item.carrierType || !item.carrierService)
        ) {
          return false;
        }

        let validdAddress = true;
        curSpecItem.spec.shippings.forEach((a) => {
          if (!a.fulfillment.shippingInfo.estimatedDeliveryTime) {
            validdAddress = false;
          }
        });
        return validdAddress;
      },

      persistShipments: async (
        payload: {
          quantityItems?: {
            specItem: JobSpecItem;
            useModel?: boolean;
            overrideJobShippingSpec?: JobShippingSpec;
            overrideAmount?: number;
          }[];
          onlyValidate?: boolean;
          quantityItemSerialNumber?: number;
          updateInternalDistribution?: boolean;
        },
        state
      ): Promise<{ success: boolean; failedSpecItem: JobSpecItem | null }> => {
        // validate
        let validateResult = true;
        let failedItem: JobSpecItem | null = null;
        const { configItem } = state.jobConfig;
        const quantityItems =
          payload.quantityItems ||
          configItem.specItems.map((specItem) => {
            return {
              specItem: specItem,
              useModel: true,
            };
          });

        each(
          quantityItems,
          async (quantityItem: {
            specItem: JobSpecItem;
            useModel?: boolean;
            overrideJobShippingSpec?: JobShippingSpec;
            overrideAmount?: number;
          }) => {
            if (validateResult) {
              const res = jobConfig.validateSpecItemShipments({
                specItem: quantityItem.specItem,
                useModel: quantityItem.useModel,
                overrideJobShippingSpec: quantityItem.overrideJobShippingSpec,
                overrideAmount: quantityItem.overrideAmount,
              });

              if (!res) {
                validateResult = false;
                failedItem = quantityItem.specItem;
                return false;
              }
            }
          }
        );

        if (payload.onlyValidate) {
          return {
            success: validateResult,
            failedSpecItem: failedItem,
          };
        }
        // saving
        if (validateResult) {
          const promises: object[] = [];

          if (!payload.quantityItems) {
            await dispatch.jobConfig.updateShipment({
              updateInternalDistribution: payload.updateInternalDistribution,
            });
          } else {
            each(
              quantityItems,
              (quantityItem: {
                specItem: JobSpecItem;
                overrideJobShippingSpec?: JobShippingSpec;
              }) => {
                if (payload.quantityItemSerialNumber !== undefined) {
                  if (
                    payload.quantityItemSerialNumber ===
                    quantityItem.specItem.serialNumber
                  ) {
                    promises.push(
                      dispatch.jobConfig.updateShipment({
                        specItem: quantityItem.specItem,
                        param: quantityItem.overrideJobShippingSpec,
                        updateInternalDistribution:
                          payload.updateInternalDistribution,
                      })
                    );
                  }
                } else {
                  promises.push(
                    dispatch.jobConfig.updateShipment({
                      specItem: quantityItem.specItem,
                      param: quantityItem.overrideJobShippingSpec,
                      updateInternalDistribution:
                        payload.updateInternalDistribution,
                    })
                  );
                }
              }
            );

            await Promise.all(promises);
          }

          return {
            success: true,
            failedSpecItem: null,
          };
        } else {
          return {
            success: false,
            failedSpecItem: failedItem,
          };
        }
      },

      updateShippingCalendars: async (payload: {
        jobShippingSpec: JobShippingSpec;
        specItem: JobSpecItem;
        param: SearchCalendarParam;
      }) => {
        const newCalendar = await jobconfigService.searchCalendar(
          payload.param
        );
        if (!newCalendar || newCalendar.length === 0) {
          return;
        }

        // update shipping
        const { jobShippingSpec, specItem } = payload;

        const firstService = newCalendar?.[0]?.dates?.[0]?.services?.[0];

        const minPriceDate = minBy(newCalendar?.[0]?.dates || [], (date) => {
          const services = date.services || [];
          let minChargeService: ShippingCalendarDateService | undefined;

          if (
            date?.services.find(
              (sItem) =>
                !sItem.rush && sItem.carrierType !== CarrierTypeEnum.Nonstandard
            )?.shippingPrice
          ) {
            minChargeService = minBy(
              services.filter(
                (sItem) =>
                  !sItem.rush &&
                  sItem.carrierType !== CarrierTypeEnum.Nonstandard
              ),
              (s) => s.shippingPrice
            );
          } else if (
            date?.services.find(
              (sItem) => sItem.carrierType !== CarrierTypeEnum.Nonstandard
            )?.shippingPrice
          ) {
            minChargeService = minBy(
              date?.services.filter(
                (sItem) => sItem.carrierType !== CarrierTypeEnum.Nonstandard
              ),
              (s) => s.shippingPrice
            );
          } else if (
            date?.services.find((sItem) => !sItem.rush && !sItem.tbd)
              ?.shippingPrice ||
            jobShippingSpec?.fulfillment?.revshopShippingMarkupPct === -100
          ) {
            minChargeService = minBy(
              services.filter((sItem) => !sItem.rush && !sItem.tbd),
              (s) => s.shippingPrice
            );
          } else {
            minChargeService = minBy(services, (s) => s.shippingPrice);
          }

          return minChargeService?.shippingPrice;
        });

        const minService = minBy(minPriceDate?.services || [], (s) => {
          return s.shippingPrice;
        });

        if (minService) {
          // minService.selected = true;
        }
        const selectedService =
          find(
            find(newCalendar?.[0]?.dates || [], (date) => date.selected)
              ?.services || [],
            (s) => s.selected
          ) ||
          minService ||
          firstService;

        jobConfig.updateShippingCalendarReducer({
          newCalendar: newCalendar[0],
          quantityItemSerialNumber: payload.param.serialNumber,
          shippingItemSerialNumber: payload.jobShippingSpec?.serialNumber || 0,
        });

        jobConfig.updateShipmentReducer({
          specItem: specItem,
          shipping: {
            expectedDeliveryDate: selectedService?.deliveryDateTime,
            fulfillment: {
              ...jobShippingSpec.fulfillment,
              // estimatedShipTime: selectedService?.estimatedShipTime,
              // shippingCost: selectedService?.shippingTotalCharge,
              freightCharge: selectedService?.shippingPrice,
              shippingInfo: {
                ...jobShippingSpec.fulfillment.shippingInfo,
                estimatedDeliveryTime: selectedService.deliveryDateTime,
              },
            },
            serialNumber: jobShippingSpec.serialNumber,
            carrierService: selectedService?.carrierService,
            carrierType: selectedService?.carrierType,
            shipmentType: selectedService?.shipmentType,
            // estimatedDeliveryTime: selectedService?.deliveryDateTime,
            // estimatedShipTime: selectedService?.estimatedShipTime,
          },
        });
      },

      updateShippingNote: async (payload: string, state) => {
        const configItem = state.jobConfig.configItem;
        try {
          const newConfigItem = await jobconfigService.basicUpdate(
            configItem.id,
            {
              shippingNote: payload,
            }
          );

          jobConfig.setState({
            configItem: {
              ...configItem,
              shippingNote: newConfigItem.shippingNote,
            },
          });
        } catch {}
      },

      updateHardCopyAddress: async (
        payload: {
          contactId: number;
          addrId: number;
        },
        state
      ) => {
        const configItem = state.jobConfig.configItem;
        const { contactId, addrId } = payload;
        const newConfig = await jobconfigService.basicUpdate(configItem.id, {
          proofContactId: contactId,
          proofContactAddressId: addrId,
          proofType: PROOF_TYPE_ENUM.HARD_COPY,
        });

        jobConfig.setState({
          configItem: newConfig,
        });
      },

      updateJobRemark: async (payload: string, state) => {
        const configItem = state.jobConfig.configItem;
        try {
          const newConfigItem = await jobconfigService.basicUpdate(
            configItem.id,
            {
              remark: payload,
            }
          );

          jobConfig.setState({
            configItem: newConfigItem,
          });
        } catch {}
      },

      updateJobProofType: async (
        payload: {
          contactId?: number;
          addrId?: number;
          // type?: PROOF_TYPE_ENUM;
          digitalProofRequired?: boolean;
          hardcopyProofRequired?: boolean;
        },
        state
      ) => {
        const {
          contactId,
          addrId,
          digitalProofRequired,
          hardcopyProofRequired,
        } = payload;
        const configItem = state.jobConfig.configItem;
        const addressObj =
          typeof hardcopyProofRequired === 'boolean'
            ? !hardcopyProofRequired
              ? {
                  proofContactId: 0,
                  proofContactAddressId: 0,
                }
              : {
                  proofContactId: contactId,
                  proofContactAddressId: addrId,
                  hardcopyProofRequired,
                }
            : {};
        try {
          const newConfigItem = await jobconfigService.basicUpdate(
            configItem.id,
            {
              hardcopyProofRequired,
              digitalProofRequired,
              ...addressObj,
            }
          );

          await jobConfig.getShippingCalendar({});

          jobConfig.setState({
            configItem: newConfigItem,
          });
        } catch {}
      },

      updateShippingOrderDate: async (payload: string, state) => {
        const configItem = state.jobConfig.configItem;
        try {
          const newConfigItem = await jobconfigService.basicUpdate(
            configItem.id,
            {
              orderDate: payload,
            }
          );

          await jobConfig.getShippingCalendar({});
          jobConfig.setState({
            configItem: newConfigItem,
          });
        } catch {}
      },

      getPriceExpression: async (payload: string, state) => {
        const expressions = await jobconfigService.getPriceExpression(payload);
        jobConfig.setState({ expressions });
      },

      getCompareWithOrg: async (payload: CompareWithOrgParam, state) => {
        return jobconfigService.getCompareWithOrg(payload);
      },

      addShared: async (payload: AddSharedParam, state) => {
        try {
          const newConfigItem = await jobconfigService.addShared(payload);

          jobConfig.setState({
            configItem: newConfigItem,
          });
        } catch {}
      },

      deleteShared: async (payload: DeleteSharedParam) => {
        try {
          const newConfigItem = await jobconfigService.deleteShared(payload);

          jobConfig.setState({
            configItem: newConfigItem,
          });
        } catch {}
      },

      sendShareList: async (payload: SendSharedListParam) => {
        return jobconfigService.sendShareList(payload);
      },

      changeJobName: async (payload: ChangeJobNameParam) => {
        try {
          const newConfigItem = await jobconfigService.changeJobName(payload);
          jobConfig.setState({
            configItem: newConfigItem,
          });
        } catch {}
      },

      getUserAvlContacts: async (payload, state) => {
        const contacts = await contactService.getUserAvlContacts(
          state.user?.userId
        );

        jobConfig.setState({
          userAvlContacts: contacts,
        });

        return contacts;
      },

      getOptionPaper: async (payload: OptionPapers, state) => {
        const paper = state.jobConfig.optionPaper[payload.searchScope]?.find(
          (item) =>
            item.jobConfigId === payload.jobConfigId &&
            item.targetSkuId === payload.targetSkuId
        );
        if (payload && (!paper || !paper?.list.length)) {
          const list = await jobconfigService.getOptionPaper(payload);
          const optionPaperItem: JobConfigState['optionPaper'] = cloneDeep(
            state.jobConfig.optionPaper
          );
          if (!optionPaperItem[payload.searchScope]) {
            optionPaperItem[payload.searchScope] = [];
          }
          optionPaperItem[payload.searchScope]?.push({
            jobConfigId: payload.jobConfigId,
            targetSkuId: payload.targetSkuId,
            list,
          });
          jobConfig.setState({
            optionPaper: optionPaperItem,
          });
        } else {
          await new Promise((resolve) =>
            setTimeout(() => resolve('success'), 500)
          );
        }
      },
      getMaterialsBasePaperOptions: async (payload, state) => {
        if (!Object.keys(state.jobConfig.filterOptionPaper).length) {
          // const res = await jobconfigService.getMaterialsBasePaperOptions();
          // jobConfig.setState({
          //   filterOptionPaper: res,
          // });
        }
      },
      confirmUpdateToCart: async ({
        jobId,
        id,
        serialNumber,
      }: {
        jobId: number;
        id: string;
        serialNumber: number;
      }) => {
        const oldOrderItem = await jobconfigService.getJobConfigFromCache(id);

        const newOrderItem = await jobconfigService.confirmUpdateToCart(id);
        // The design brought from Canva has been committed to an order, so the shop stops
        // offering to put it on another product. Done here rather than in the buttons because
        // several of them reach the cart, and a design still on offer after checkout would
        // silently attach itself to the next product the buyer opens.
        clearCanvaSessionDraftId();

        updatePriceMessage({
          oldOrderItem,
          newOrderItem,
          serialNumber,
        });
      },
      addJobQuantityToCart: async ({
        jobId,
        serialNumber,
      }: {
        jobId: string;
        serialNumber: number;
        userId?: number;
        customerOrgId?: number;
      }) => {
        const oldOrderItem = await jobconfigService.getJobConfigFromCache(
          jobId
        );

        const newOrderItem = await jobconfigService.addJobQuantityToCart(
          jobId,
          serialNumber
        );
        // The design brought from Canva has been committed to an order, so the shop stops
        // offering to put it on another product. Done here rather than in the buttons because
        // several of them reach the cart, and a design still on offer after checkout would
        // silently attach itself to the next product the buyer opens.
        clearCanvaSessionDraftId();

        updatePriceMessage({
          oldOrderItem,
          newOrderItem,
          serialNumber,
        });

        return newOrderItem;
      },

      startJobConfig: async ({
        skuId,
        quantity,
        isExpert,
        directlyAddToCart,
      }: StartJobConfigParam) => {
        const configItem = await jobconfigService.startJobConfig({
          skuId: skuId,
          quantity: quantity,
          isExpert: isExpert,
          directlyAddToCart: directlyAddToCart,
        });
        return configItem;
      },

      directlyAddToCart: async ({
        skuId,
        quantity,
        isExpert,
        directlyAddToCart,
      }: StartJobConfigParam) => {
        const cartLineItem = await jobconfigService.directlyAddToCart({
          skuId: skuId,
          quantity: quantity,
          isExpert: isExpert,
          directlyAddToCart: directlyAddToCart,
        });
        return cartLineItem;
      },

      summerUpdateJobConfig: async (payload: Partial<JobConfigViewer>) => {
        const configItem = await jobconfigService.summerUpdateJobConfig(
          payload
        );
        return configItem;
      },

      summerAddToCart: async (payload: Partial<JobConfigViewer>) => {
        const cartItem = await jobconfigService.summerAddToCart(payload);
        return cartItem;
      },

      summerStartJobConfig: async ({
        skuId,
        quantity,
      }: Partial<StartJobConfigParam>) => {
        const configItem = await jobconfigService.summerStartJobConfig({
          skuId: skuId,
          quantity: quantity,
        });
        return configItem;
      },

      summeryDeleteJobConfig: async (id: string) => {
        const configItem = await jobconfigService.summeryDeleteJobConfig(id);
        return configItem;
      },

      sendToSourcer: async (jobConfigId: string) => {
        return await jobconfigService.sendToSourcer(jobConfigId);
      },

      jobRevisionComplete: async (revisionId: number) => {
        return await jobconfigService.jobRevisionComplete(revisionId);
      },

      jobRevisionAdd: async (jobId: number) => {
        return await jobconfigService.jobRevisionAdd(jobId);
      },

      jobRevisionCancel: async (revisionId: number) => {
        return await jobconfigService.jobRevisionCancel(revisionId);
      },

      completeFromEstimator: async ({
        revisionId,
        comparatorItemSerialNumber,
        quantityItemSerialNumber,
      }: {
        revisionId: number;
        comparatorItemSerialNumber: number;
        quantityItemSerialNumber: number;
      }) => {
        return await jobconfigService.completeFromEstimator(
          revisionId,
          comparatorItemSerialNumber,
          quantityItemSerialNumber
        );
      },
      createConfirmPaper: async (
        payload: { quantitySerialNumber: number },
        state
      ) => {
        try {
          jobConfig.setRevxSpinloading(true);
          const res = await jobEstimatorService.createConfirmPaper(
            state.jobConfig.configItem.id,
            payload.quantitySerialNumber
          );
          jobConfig.setState({
            paperSelectionVisible: true,
            paperSelectionSource: res,
          });
        } finally {
          jobConfig.setRevxSpinloading(false);
        }
      },
      updateConfirmPaper: async (
        payload: JobEstimatorConfirmPaperUpdateParams,
        state
      ) => {
        try {
          jobConfig.setRevxSpinloading(true);
          const res = await jobEstimatorService.updateConfirmPaper(
            state.jobConfig.configItem.id,
            payload
          );
          jobConfig.setState({
            paperSelectionSource: res,
          });
        } finally {
          jobConfig.setRevxSpinloading(false);
        }
      },
    };
  },
});
